/**
 * Job creation modal — new build or repair job.
 * Contact selector with + New. Sell price defaults to parent item list_price.
 * Parent item description shown instead of work_description field.
 * Customer PO#, Work Order#, Sales Order# fields.
 * Design Job checkbox: flags job as is_design_job, sets status to in_design.
 * DNV prompt: shown when is_design_job + parent item is DNV-flagged.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createJob, fetchCustomers, fetchParentItems, fetchCustomerContacts, fetchBomVersions, createDnvProcess } from '../../api/jobs';
import { createCustomer, createContact } from '../../api/customers';
import { createParentItem } from '../../api/parentItems';
import { Modal, Button, SlideOutPanel, SearchableSelect } from '../common';

const ALL_DNV_PROCESSES = [
  { key: 'engineering_review', label: 'Engineering Review & Stamp' },
  { key: 'lrqa_quote', label: 'LRQA Type Approval Quote' },
  { key: 'lrqa_type_approval', label: 'LRQA Type Approval' },
  { key: 'fabrication', label: 'Fabrication' },
  { key: 'drop_test', label: 'Drop Test' },
  { key: 'lrqa_production_inspection', label: 'LRQA Production Inspection' },
  { key: 'final_lrqa_inspection', label: 'Final LRQA Inspection' },
];

export default function JobCreateModal({ open, onClose }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    job_type: 'build',
    is_design_job: false,
    customer: '',
    customer_contact: '',
    parent_item: '',
    bom_version: '',
    quantity: 1,
    sell_price: '',
    target_due_date: '',
    priority: 'medium',
    notes: '',
    customer_po_number: '',
    workorder_number: '',
    sales_order_number: '',
    quote_number: '',
    // V2-a2 Part A: projected design hours per selectable phase, only
    // used when is_design_job is checked. Empty string = "not entered"
    // → phase row skipped at creation (Stage D flags source=unknown).
    design_hours_csd: '',
    design_hours_plc: '',
  });
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [parentItems, setParentItems] = useState([]);
  const [bomVersions, setBomVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Parent item description display
  const [selectedParentItem, setSelectedParentItem] = useState(null);

  // DNV process pre-selection (for design jobs with DNV parent items)
  const [selectedDnvProcesses, setSelectedDnvProcesses] = useState(new Set());

  // Parent item typeahead
  const [piSearch, setPiSearch] = useState('');
  const [piOpen, setPiOpen] = useState(false);

  // New customer slide-out
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCustForm, setNewCustForm] = useState({
    name: '', shipping_address_line1: '', shipping_address_line2: '',
    shipping_city: '', shipping_state: '', shipping_zip: '',
    notes: '', tags: '',
  });
  const [newCustSaving, setNewCustSaving] = useState(false);

  // New contact slide-out
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [newContactForm, setNewContactForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', role_title: '', is_primary: false,
  });
  const [newContactSaving, setNewContactSaving] = useState(false);

  // New parent item slide-out
  const [newPiOpen, setNewPiOpen] = useState(false);
  const [newPiForm, setNewPiForm] = useState({
    parent_item_no: '', description: '', estimated_labor_hours: '', list_price: '', is_generic: false,
  });
  const [newPiSaving, setNewPiSaving] = useState(false);

  const loadDropdowns = useCallback(async () => {
    try {
      const [custData, piData] = await Promise.all([
        fetchCustomers(),
        fetchParentItems(),
      ]);
      setCustomers(custData.data || custData.results || custData || []);
      setParentItems(piData.data || piData.results || piData || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (open) {
      loadDropdowns();
      setForm({
        job_type: 'build', is_design_job: false, customer: '', customer_contact: '', parent_item: '',
        bom_version: '',
        quantity: 1, sell_price: '', target_due_date: '', priority: 'medium', notes: '',
        customer_po_number: '', workorder_number: '', sales_order_number: '', quote_number: '',
        design_hours_csd: '', design_hours_plc: '',
      });
      setSelectedParentItem(null);
      setSelectedDnvProcesses(new Set());
      setBomVersions([]);
      setPiSearch('');
      setPiOpen(false);
      setError(null);
    }
  }, [open, loadDropdowns]);

  // When customer changes, load contacts
  useEffect(() => {
    if (form.customer) {
      fetchCustomerContacts({ customer: form.customer })
        .then((data) => setContacts(data.data || data.results || data || []))
        .catch(() => setContacts([]));
      setForm((prev) => ({ ...prev, customer_contact: '' }));
    } else {
      setContacts([]);
    }
  }, [form.customer]);

  // When parent item changes, set sell price, track description, and load BOM versions
  useEffect(() => {
    if (form.parent_item) {
      const pi = parentItems.find((p) => p.id === form.parent_item);
      setSelectedParentItem(pi || null);
      if (pi?.list_price) {
        setForm((prev) => ({ ...prev, sell_price: pi.list_price, bom_version: '' }));
      } else {
        setForm((prev) => ({ ...prev, bom_version: '' }));
      }
      fetchBomVersions({ parent_item: form.parent_item, page_size: 50 })
        .then((versions) => {
          setBomVersions(versions);
          const defaultV = versions.find((v) => v.is_default) || versions[0];
          if (defaultV) setForm((prev) => ({ ...prev, bom_version: defaultV.id }));
        })
        .catch(() => setBomVersions([]));
    } else {
      setSelectedParentItem(null);
      setBomVersions([]);
      setForm((prev) => ({ ...prev, bom_version: '' }));
    }
  }, [form.parent_item, parentItems]);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleDnvProcess(key) {
    setSelectedDnvProcesses((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const showDnvPanel = form.is_design_job && form.job_type === 'build' && selectedParentItem?.is_dnv;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.customer || !form.parent_item) {
      setError('Customer and parent item are required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = { ...form };
      payload.work_description = selectedParentItem?.description || '';
      if (!payload.sell_price) delete payload.sell_price;
      if (!payload.target_due_date) delete payload.target_due_date;
      if (!payload.customer_contact) delete payload.customer_contact;
      if (!payload.notes) delete payload.notes;
      if (!payload.customer_po_number) delete payload.customer_po_number;
      if (!payload.workorder_number) delete payload.workorder_number;
      if (!payload.sales_order_number) delete payload.sales_order_number;
      if (!payload.quote_number) delete payload.quote_number;
      // Design jobs skip BOM snapshot — don't send bom_version
      if (payload.is_design_job) delete payload.bom_version;
      else if (!payload.bom_version) delete payload.bom_version;

      // V2-a2 Part A: pack projected design hours into a nested
      // design_phases array only when is_design_job and at least one
      // value was entered. Empty inputs → phase row omitted → Stage D
      // will tag that phase 'unknown' and leave the date NULL.
      if (payload.is_design_job) {
        const phases = [];
        if (payload.design_hours_csd !== '' && payload.design_hours_csd != null) {
          phases.push({
            phase_type: 'complete_system_drawing',
            projected_hours: parseFloat(payload.design_hours_csd),
            sort_order: 0,
          });
        }
        if (payload.design_hours_plc !== '' && payload.design_hours_plc != null) {
          phases.push({
            phase_type: 'plc_programming',
            projected_hours: parseFloat(payload.design_hours_plc),
            sort_order: 1,
          });
        }
        if (phases.length > 0) payload.design_phases = phases;
      }
      // Remove the flat form-only fields — server sees design_phases only.
      delete payload.design_hours_csd;
      delete payload.design_hours_plc;

      const job = await createJob(payload);

      // If DNV processes were selected, create them on the new job
      if (showDnvPanel && selectedDnvProcesses.size > 0) {
        await Promise.all(
          Array.from(selectedDnvProcesses).map((process_type) =>
            createDnvProcess({ job: job.id, process_type, is_required: true }).catch(() => {})
          )
        );
      }

      onClose?.();
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  }

  // --- New Customer ---
  function openNewCustomer() {
    setNewCustForm({
      name: '', shipping_address_line1: '', shipping_address_line2: '',
      shipping_city: '', shipping_state: '', shipping_zip: '', notes: '', tags: '',
    });
    setNewCustOpen(true);
  }

  async function handleSaveNewCustomer() {
    if (!newCustForm.name.trim()) { alert('Customer name is required.'); return; }
    setNewCustSaving(true);
    try {
      const payload = {
        ...newCustForm,
        tags: newCustForm.tags ? newCustForm.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };
      const created = await createCustomer(payload);
      setCustomers((prev) => [...prev, created]);
      setForm((prev) => ({ ...prev, customer: created.id }));
      setNewCustOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to create customer');
    } finally { setNewCustSaving(false); }
  }

  // --- New Contact ---
  function openNewContact() {
    setNewContactForm({ first_name: '', last_name: '', email: '', phone: '', role_title: '', is_primary: false });
    setNewContactOpen(true);
  }

  async function handleSaveNewContact() {
    if (!newContactForm.first_name.trim()) { alert('First name is required.'); return; }
    setNewContactSaving(true);
    try {
      const created = await createContact({ ...newContactForm, customer: form.customer });
      setContacts((prev) => [...prev, created]);
      setForm((prev) => ({ ...prev, customer_contact: created.id }));
      setNewContactOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to create contact');
    } finally { setNewContactSaving(false); }
  }

  // --- New Parent Item ---
  async function handleSaveNewParentItem() {
    if (!newPiForm.parent_item_no.trim()) { alert('Parent item number is required.'); return; }
    setNewPiSaving(true);
    try {
      const payload = {
        parent_item_no: newPiForm.parent_item_no.trim(),
        description: newPiForm.description.trim(),
        is_generic: newPiForm.is_generic,
        estimated_labor_hours: newPiForm.estimated_labor_hours ? parseFloat(newPiForm.estimated_labor_hours) : 0,
        list_price: newPiForm.list_price ? parseFloat(newPiForm.list_price) : 0,
      };
      const created = await createParentItem(payload);
      setParentItems((prev) => [...prev, created]);
      handleChange('parent_item', created.id);
      setPiSearch(`${created.parent_item_no} — ${created.description || ''}`);
      setSelectedParentItem(created);
      setNewPiOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to create parent item');
    } finally { setNewPiSaving(false); }
  }

  // Filter parent items for repair (generic only)
  const filteredParentItems = form.job_type === 'repair'
    ? parentItems.filter((pi) => pi.is_generic)
    : parentItems;

  return (
    <Modal open={open} onClose={onClose} title="Create New Job" maxWidth="max-w-xl">
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Job type toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
          <div className="flex gap-2">
            {['build', 'repair'].map((type) => (
              <button key={type} type="button" onClick={() => {
                handleChange('job_type', type);
                if (type !== 'build') handleChange('is_design_job', false);
              }}
                className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors ${
                  form.job_type === type ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}>{type === 'build' ? 'Build' : 'Repair'}</button>
            ))}
          </div>
        </div>

        {/* Design Job checkbox — build jobs only */}
        {form.job_type === 'build' && (
          <div className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
            form.is_design_job ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'
          }`}>
            <input
              type="checkbox"
              id="is_design_job"
              checked={form.is_design_job}
              onChange={(e) => handleChange('is_design_job', e.target.checked)}
              className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 rounded cursor-pointer"
            />
            <label htmlFor="is_design_job" className="cursor-pointer select-none">
              <span className="text-sm font-medium text-gray-800">✏️ Design Job</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Job is in active design — no BOM available yet. BOM will be imported and pushed to the job later.
              </p>
            </label>
          </div>
        )}

        {/* Customer + Add New */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
          <div className="flex gap-2">
            <div className="flex-1">
              <SearchableSelect
                value={form.customer}
                onChange={(v) => handleChange('customer', v)}
                options={customers.map((c) => ({ value: c.id, label: c.name }))}
                placeholder="Search customers..."
                emptyMessage="No customers match"
                onCreateNew={(searchText) => {
                  setNewCustForm({ ...newCustForm, name: searchText });
                  setNewCustOpen(true);
                }}
                createNewLabel="+ Create Customer"
              />
            </div>
          </div>
        </div>

        {/* Contact + Add New (only when customer is selected) */}
        {form.customer && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
            <div className="flex gap-2">
              <select className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.customer_contact} onChange={(e) => handleChange('customer_contact', e.target.value)}>
                <option value="">Select contact...</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.first_name || ''} ${c.last_name || ''}`.trim() || '(unnamed)'} {c.is_primary ? '(Primary)' : ''}
                  </option>
                ))}
              </select>
              <button type="button" onClick={openNewContact} className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 whitespace-nowrap">+ New</button>
            </div>
          </div>
        )}

        {/* Parent Item — typeahead combobox */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item</label>
          <input
            type="text"
            autoComplete="off"
            placeholder="Type part number or description..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            value={piSearch}
            onChange={(e) => {
              setPiSearch(e.target.value);
              setPiOpen(true);
              if (form.parent_item) handleChange('parent_item', '');
              setSelectedParentItem(null);
            }}
            onFocus={() => setPiOpen(true)}
            onBlur={() => setTimeout(() => setPiOpen(false), 150)}
          />
          {piOpen && (() => {
            const q = piSearch.toLowerCase();
            const matches = filteredParentItems.filter((pi) =>
              !q ||
              pi.parent_item_no.toLowerCase().includes(q) ||
              (pi.description || '').toLowerCase().includes(q)
            ).slice(0, 20);
            const showCreate = piSearch.trim().length > 0;
            return (matches.length > 0 || showCreate) ? (
              <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                {matches.map((pi) => (
                  <li
                    key={pi.id}
                    onMouseDown={() => {
                      handleChange('parent_item', pi.id);
                      setPiSearch(`${pi.parent_item_no} — ${pi.description}`);
                      setPiOpen(false);
                    }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${
                      form.parent_item === pi.id ? 'bg-blue-50 font-medium' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium text-gray-900">{pi.parent_item_no}</span>
                      {pi.description && <span className="text-gray-500 text-xs">{pi.description}</span>}
                      {pi.is_generic && <span className="text-purple-500 text-xs">(Generic)</span>}
                      {pi.is_dnv && <span className="text-blue-600 text-xs font-medium">🏷 DNV</span>}
                    </div>
                  </li>
                ))}
                {matches.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-400 italic">No matches found</li>
                )}
                {showCreate && (
                  <li
                    onMouseDown={() => {
                      setNewPiForm({
                        parent_item_no: piSearch.trim(),
                        description: '', estimated_labor_hours: '', list_price: '',
                        is_generic: form.job_type === 'repair',
                      });
                      setNewPiOpen(true);
                      setPiOpen(false);
                    }}
                    className="px-3 py-2 text-sm cursor-pointer text-blue-600 hover:bg-blue-50 font-medium border-t border-gray-100"
                  >
                    + Create Parent Item: "{piSearch.trim()}"
                  </li>
                )}
              </ul>
            ) : null;
          })()}
          {selectedParentItem && (
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-100 flex-1">
                <span className="font-medium">Description:</span> {selectedParentItem.description}
              </p>
              {selectedParentItem.is_dnv && (
                <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded border border-blue-200 flex-shrink-0">
                  🏷 DNV 2.71
                </span>
              )}
            </div>
          )}
        </div>

        {/* DNV Process selector — shown when design job + DNV parent item */}
        {showDnvPanel && (
          <div className="border border-blue-200 rounded-lg bg-blue-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-bold text-blue-800">🏷 DNV 2.71 — Select Required Processes</span>
            </div>
            <p className="text-xs text-blue-600 mb-3">
              Check which certification steps apply to this job. You can toggle these on/off later in the job's Design tab.
            </p>
            <div className="space-y-1.5">
              {ALL_DNV_PROCESSES.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedDnvProcesses.has(key)}
                    onChange={() => toggleDnvProcess(key)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">{label}</span>
                </label>
              ))}
            </div>
            {selectedDnvProcesses.size === 0 && (
              <p className="text-xs text-blue-500 mt-2 italic">
                No steps selected — you can add them from the Design tab after creation.
              </p>
            )}
          </div>
        )}

        {/* BOM Version selector — only for non-design jobs with multiple versions */}
        {!form.is_design_job && bomVersions.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">BOM Version</label>
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-0.5">
              {bomVersions.map((v) => {
                const isSelected = form.bom_version === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => handleChange('bom_version', v.id)}
                    className={`w-full text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900">
                        v{v.version_number}{v.version_label ? ` — ${v.version_label}` : ''}
                      </span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {v.is_default && (
                          <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Default</span>
                        )}
                        {isSelected && (
                          <span className="text-blue-600 text-xs">✓</span>
                        )}
                      </div>
                    </div>
                    {v.created_reason && (
                      <p className="mt-0.5 text-xs text-gray-500 whitespace-pre-wrap">{v.created_reason}</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Design job BOM note */}
        {form.is_design_job && (
          <div className="text-xs text-purple-600 bg-purple-50 border border-purple-200 px-3 py-2 rounded flex items-center gap-1.5">
            <span>ℹ️</span>
            <span>BOM will be empty at creation. After completing the design, import the BOM to the parent item and use "Push BOM" in the Design tab.</span>
          </div>
        )}

        {/* V2-a2 Part A: projected design hours per category. Both
            optional — leave blank when you don't know yet; Stage D
            will show "needs estimate" instead of a fake completion
            date. Editing later in the DesignTab triggers a recalc. */}
        {form.is_design_job && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Design hours (complete system drawing)
                <span className="text-xs font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <input
                type="number" step="0.25" min="0"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={form.design_hours_csd}
                onChange={(e) => handleChange('design_hours_csd', e.target.value)}
                placeholder="e.g. 12"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PLC programming hours
                <span className="text-xs font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <input
                type="number" step="0.25" min="0"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={form.design_hours_plc}
                onChange={(e) => handleChange('design_hours_plc', e.target.value)}
                placeholder="e.g. 6"
              />
            </div>
          </div>
        )}

        {/* Quantity & Sell Price */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input type="number" min="1" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.quantity} onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 1)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sell Price
              {selectedParentItem?.list_price > 0 && (
                <span className="text-xs text-gray-400 ml-1">(List: ${parseFloat(selectedParentItem.list_price).toFixed(2)})</span>
              )}
            </label>
            <input type="number" step="0.01" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.sell_price} onChange={(e) => handleChange('sell_price', e.target.value)} />
          </div>
        </div>

        {/* Reference numbers */}
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quote#</label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.quote_number} onChange={(e) => handleChange('quote_number', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sales Order#</label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.sales_order_number} onChange={(e) => handleChange('sales_order_number', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Work Order#</label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.workorder_number} onChange={(e) => handleChange('workorder_number', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer PO#</label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.customer_po_number} onChange={(e) => handleChange('customer_po_number', e.target.value)} />
          </div>
        </div>

        {/* Due Date & Priority */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Due Date</label>
            <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.target_due_date} onChange={(e) => handleChange('target_due_date', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={form.priority} onChange={(e) => handleChange('priority', e.target.value)}>
              <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />
        </div>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{error}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={loading}>
            {form.is_design_job ? 'Create Design Job' : 'Create Job'}
          </Button>
        </div>
      </form>

      {/* New Customer slide-out */}
      <SlideOutPanel open={newCustOpen} onClose={() => setNewCustOpen(false)} title="Add New Customer" width="max-w-lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
            <input type="text" className="w-full border rounded-md px-3 py-2 text-sm" value={newCustForm.name} onChange={(e) => setNewCustForm({ ...newCustForm, name: e.target.value })} autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
            <input type="text" className="w-full border rounded-md px-3 py-2 text-sm" value={newCustForm.shipping_address_line1} onChange={(e) => setNewCustForm({ ...newCustForm, shipping_address_line1: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
            <input type="text" className="w-full border rounded-md px-3 py-2 text-sm" value={newCustForm.shipping_address_line2} onChange={(e) => setNewCustForm({ ...newCustForm, shipping_address_line2: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label><input type="text" className="w-full border rounded-md px-3 py-2 text-sm" value={newCustForm.shipping_city} onChange={(e) => setNewCustForm({ ...newCustForm, shipping_city: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">State</label><input type="text" className="w-full border rounded-md px-3 py-2 text-sm" value={newCustForm.shipping_state} onChange={(e) => setNewCustForm({ ...newCustForm, shipping_state: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Zip</label><input type="text" className="w-full border rounded-md px-3 py-2 text-sm" value={newCustForm.shipping_zip} onChange={(e) => setNewCustForm({ ...newCustForm, shipping_zip: e.target.value })} /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label><textarea className="w-full border rounded-md px-3 py-2 text-sm" rows={2} value={newCustForm.notes} onChange={(e) => setNewCustForm({ ...newCustForm, notes: e.target.value })} /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label><input type="text" className="w-full border rounded-md px-3 py-2 text-sm" placeholder="e.g. oil_gas, repeat_customer" value={newCustForm.tags} onChange={(e) => setNewCustForm({ ...newCustForm, tags: e.target.value })} /></div>
          <div className="flex gap-2 pt-3 border-t">
            <Button loading={newCustSaving} onClick={handleSaveNewCustomer}>Create Customer</Button>
            <Button variant="ghost" onClick={() => setNewCustOpen(false)}>Cancel</Button>
          </div>
        </div>
      </SlideOutPanel>

      {/* New Contact slide-out */}
      <SlideOutPanel open={newContactOpen} onClose={() => setNewContactOpen(false)} title="Add New Contact" width="max-w-md">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs text-gray-500 mb-0.5">First Name *</label><input type="text" className="w-full border rounded px-2 py-1.5 text-sm" value={newContactForm.first_name} onChange={(e) => setNewContactForm({ ...newContactForm, first_name: e.target.value })} autoFocus /></div>
            <div><label className="block text-xs text-gray-500 mb-0.5">Last Name</label><input type="text" className="w-full border rounded px-2 py-1.5 text-sm" value={newContactForm.last_name} onChange={(e) => setNewContactForm({ ...newContactForm, last_name: e.target.value })} /></div>
          </div>
          <div><label className="block text-xs text-gray-500 mb-0.5">Email</label><input type="email" className="w-full border rounded px-2 py-1.5 text-sm" value={newContactForm.email} onChange={(e) => setNewContactForm({ ...newContactForm, email: e.target.value })} /></div>
          <div><label className="block text-xs text-gray-500 mb-0.5">Phone</label><input type="text" className="w-full border rounded px-2 py-1.5 text-sm" value={newContactForm.phone} onChange={(e) => setNewContactForm({ ...newContactForm, phone: e.target.value })} /></div>
          <div><label className="block text-xs text-gray-500 mb-0.5">Title / Role</label><input type="text" className="w-full border rounded px-2 py-1.5 text-sm" value={newContactForm.role_title} onChange={(e) => setNewContactForm({ ...newContactForm, role_title: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newContactForm.is_primary} onChange={(e) => setNewContactForm({ ...newContactForm, is_primary: e.target.checked })} /> Primary contact</label>
          <div className="flex gap-2 pt-2 border-t">
            <Button size="sm" loading={newContactSaving} onClick={handleSaveNewContact}>Create Contact</Button>
            <Button size="sm" variant="ghost" onClick={() => setNewContactOpen(false)}>Cancel</Button>
          </div>
        </div>
      </SlideOutPanel>

      {/* New Parent Item slide-out */}
      <SlideOutPanel open={newPiOpen} onClose={() => setNewPiOpen(false)} title="Add New Parent Item" width="max-w-lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item Number *</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
              placeholder="e.g. PS-5000-HD"
              value={newPiForm.parent_item_no}
              onChange={(e) => setNewPiForm({ ...newPiForm, parent_item_no: e.target.value })}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={2}
              placeholder="Pump assembly description"
              value={newPiForm.description}
              onChange={(e) => setNewPiForm({ ...newPiForm, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Est. Labor Hours</label>
              <input
                type="number"
                step="0.25"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g. 8"
                value={newPiForm.estimated_labor_hours}
                onChange={(e) => setNewPiForm({ ...newPiForm, estimated_labor_hours: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">List Price ($)</label>
              <input
                type="number"
                step="0.01"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g. 5000.00"
                value={newPiForm.list_price}
                onChange={(e) => setNewPiForm({ ...newPiForm, list_price: e.target.value })}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={newPiForm.is_generic}
              onChange={(e) => setNewPiForm({ ...newPiForm, is_generic: e.target.checked })}
            />
            Generic item (available for repair jobs)
          </label>
          <div className="flex gap-2 pt-3 border-t border-gray-200">
            <Button loading={newPiSaving} onClick={handleSaveNewParentItem}>Create Parent Item</Button>
            <Button variant="ghost" onClick={() => setNewPiOpen(false)}>Cancel</Button>
          </div>
        </div>
      </SlideOutPanel>
    </Modal>
  );
}
